import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Eye, EyeOff, Lock, Shield } from 'lucide-react';
import { LoginForm } from '../types';
import apiService from '../services/api';

const loginSchema = yup.object({
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
  password: yup.string().required('Password is required'),
}).required();

const SystemAdminLoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({ resolver: yupResolver(loginSchema) });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setError('');
    try {
      console.log('Attempting system admin login...', data);
      const resp = await apiService.systemAdminLogin(data);
      console.log('System admin login response:', resp);

      if (resp.success && resp.token) {
        console.log('Login successful, setting localStorage and navigating...');
        localStorage.setItem('token', resp.token);
        localStorage.setItem('isSystemAdmin', 'true');
        // Set default light theme for SystemAdmin
        localStorage.setItem('theme', 'light');
        localStorage.setItem('theme:systemadmin', 'light');
        console.log('About to navigate to /systemadmin');
        navigate('/systemadmin');
      } else {
        console.log('Login failed - no token or success=false');
        throw new Error(resp.message || 'Login failed');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-accent-color rounded-lg flex items-center justify-center">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">System Admin Sign in</h2>
          <p className="mt-2 text-sm text-gray-600">For platform administrators</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
              <input {...register('email')} type="email" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent-color focus:border-accent-color sm:text-sm" placeholder="sysadmin@example.com" />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <div className="mt-1 relative">
                <input {...register('password')} type={showPassword ? 'text' : 'password'} className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-accent-color focus:border-accent-color sm:text-sm" placeholder="••••••••" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Lock className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <button type="submit" disabled={isLoading} className="w-full py-2 px-4 rounded-md text-white bg-accent-color hover:bg-accent-color-600 disabled:opacity-50">
            {isLoading ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="text-center">
            <button type="button" className="text-sm text-accent-color hover:text-accent-color-600" onClick={() => navigate('/login')}>
              Back to company user login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SystemAdminLoginPage;


